import { useState } from 'react'
import { toast } from 'sonner'
import { CalendarDays } from 'lucide-react'

import { useEmailThreadEntityCandidates, useLinkEmailThread, useUpdateEmailThread } from '@/stores/emails.store'
import type { EmailThreadEntityCandidate } from '@/gen/cortado/v1/email_conversations_pb'
import { EmailThreadReviewReason, EmailThreadStatus } from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatShortDate } from '@/lib/date-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'

interface ThreadEntityPickerProps {
  organizationId: string
  threadId: string
  /** The thread's open review question, from GetEmailThread. */
  reviewReason?: EmailThreadReviewReason
}

/** "Wedding · Jun 14, 2027 · Quote sent" */
export function candidateLabel(candidate: EmailThreadEntityCandidate): string {
  const kind = candidate.entityType === ParentTypeEnum.PARENT_TYPE_BOOKING ? 'Booking' : 'Lead'
  const parts = [candidate.eventType?.trim() || kind]
  if (candidate.eventDate) parts.push(formatShortDate(candidate.eventDate))
  if (candidate.stage) parts.push(candidate.stage)
  return parts.join(' · ')
}

/**
 * ThreadEntityPicker - asks "Which event is this about?" on a captured
 * conversation whose contact has several open leads or bookings. One click
 * links the thread to the chosen record; "None of these" answers the
 * question without linking anything. Renders nothing for any other thread.
 */
export function ThreadEntityPicker({ organizationId, threadId, reviewReason }: ThreadEntityPickerProps) {
  const isAsking = reviewReason === EmailThreadReviewReason.AMBIGUOUS_ENTITY
  const { candidates, isLoading, hasLoaded } = useEmailThreadEntityCandidates(threadId, { enabled: isAsking })
  const { link } = useLinkEmailThread()
  const { updateThread } = useUpdateEmailThread()
  const [pendingKey, setPendingKey] = useState<string | null>(null)

  if (!isAsking) return null
  if (isLoading && !hasLoaded) return <Skeleton className="h-16 w-full" />

  const handlePick = async (candidate: EmailThreadEntityCandidate) => {
    setPendingKey(candidate.entityId)
    try {
      await link({
        organizationId,
        threadId,
        entityType: candidate.entityType,
        entityId: candidate.entityId,
      })
      toast.success(`Linked to ${candidateLabel(candidate)}`)
    } catch (error) {
      reportError(error, { feature: 'email_thread_entity_pick', kind: 'mutation' })
      toast.error('Failed to link conversation', { description: getErrorDescription(error) })
    } finally {
      setPendingKey(null)
    }
  }

  const handleNone = async () => {
    setPendingKey('none')
    try {
      await updateThread({ id: threadId, status: EmailThreadStatus.OPEN, updateMask: ['status'] })
    } catch (error) {
      reportError(error, { feature: 'email_thread_entity_pick_none', kind: 'mutation' })
      toast.error('Failed to update conversation', { description: getErrorDescription(error) })
    } finally {
      setPendingKey(null)
    }
  }

  return (
    <div role="group" aria-label="Which event is this about?" className="space-y-2 rounded-md border border-dashed bg-muted/40 p-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        Which event is this about?
      </div>
      <div className="flex flex-wrap gap-2">
        {candidates.map((candidate) => (
          <Button
            key={candidate.entityId}
            type="button"
            variant="outline"
            size="sm"
            disabled={pendingKey !== null}
            onClick={() => void handlePick(candidate)}
          >
            {candidateLabel(candidate)}
          </Button>
        ))}
        <Button type="button" variant="ghost" size="sm" disabled={pendingKey !== null} onClick={() => void handleNone()}>
          None of these
        </Button>
      </div>
    </div>
  )
}
